import type { ApiClient } from "../../types";
import { clientWithApiClient } from "../../client";
import { appendRegionIds } from "../regions/params";
import { appendPaymentSource } from "../payments/source";
import type { ListResponse, Response } from "../../types";
import type {
  Customer,
  CreateCustomerPayload,
  UpdateCustomerPayload,
  ListCustomersParams,
  CustomerFilterParams,
  CustomerExportResponse,
  CustomerOverview,
  CustomerListResponse,
  CustomerTag,
  CreateCustomerTagPayload,
  UpdateCustomerTagPayload,
  CustomerSegment,
  CreateCustomerSegmentPayload,
  UpdateCustomerSegmentPayload,
  CustomerNote,
  CreateCustomerNotePayload,
  UpdateCustomerNotePayload,
  ListInteractionsParams,
  ListInteractionsResponse,
} from "./types";

/**
 * The filters that pick *which* customers, shared by the list and the export.
 *
 * One builder rather than two, so "export what I am looking at" cannot drift
 * from what the list actually shows.
 */
export const appendCustomerFilters = (
  searchParams: URLSearchParams,
  fleetId: string,
  params?: CustomerFilterParams,
): URLSearchParams => {
  searchParams.append("fleet_id", fleetId);

  // Region scope. Shared by the list and the CSV export on purpose, so
  // "export what I am looking at" stays honest while a region narrows the
  // list. Emits nothing for an absent or empty selection.
  appendRegionIds(searchParams, params?.region_ids);

  if (params?.search) {
    searchParams.append("search", params.search);
  }
  if (params?.customerType) {
    searchParams.append("customer_type", params.customerType);
  }
  if (params?.status) {
    searchParams.append("status", params.status);
  }
  if (params?.tagIds?.length) {
    searchParams.append("tag_ids", params.tagIds.join(","));
  }

  return searchParams;
};

const list = (
  apiClient: ApiClient,
  fleetId: string,
  params?: ListCustomersParams,
) => {
  const searchParams = appendCustomerFilters(
    new URLSearchParams(),
    fleetId,
    params,
  );

  if (params?.includeStats) {
    searchParams.append("include_stats", "true");
    appendPaymentSource(searchParams, params.source);
  }
  if (params?.includeTags) {
    searchParams.append("include_tags", "true");
  }
  if (params?.view) {
    searchParams.append("view", params.view);
  }
  if (params?.tab) {
    searchParams.append("tab", params.tab);
  }
  if (params?.sort) {
    searchParams.append("sort", params.sort);
  }
  if (params?.page) {
    searchParams.append("page", String(params.page));
  }
  if (params?.pageSize) {
    searchParams.append("page_size", String(params.pageSize));
  }

  const queryString = searchParams.toString();
  const url = queryString ? `/customers?${queryString}` : "/customers";

  return clientWithApiClient<CustomerListResponse>(apiClient, url);
};

/**
 * Downloads every customer matching `params` as CSV.
 *
 * Answers with the same base64 envelope the report exports use, which the
 * caller turns into a file — the shared HTTP client cannot read a binary
 * response body.
 */
const exportCsv = (
  apiClient: ApiClient,
  fleetId: string,
  params?: CustomerFilterParams,
) => {
  const searchParams = appendCustomerFilters(
    new URLSearchParams(),
    fleetId,
    params,
  );

  return clientWithApiClient<CustomerExportResponse>(
    apiClient,
    `/customers/export?${searchParams.toString()}`,
  );
};

/**
 * Retrieves a specific customer by its ID within a fleet
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the customer
 * @param id - Unique identifier of the customer
 * @returns Promise that resolves to the requested customer
 */
const get = (apiClient: ApiClient, fleetId: string, id: string) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString
    ? `/customers/${id}?${queryString}`
    : `/customers/${id}`;

  return clientWithApiClient<Response<Customer>>(apiClient, url);
};

/**
 * The customer page's numbers and next trip, built on the server in one call.
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the customer
 * @param id - Unique identifier of the customer
 */
const overview = (apiClient: ApiClient, fleetId: string, id: string) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  return clientWithApiClient<Response<CustomerOverview>>(
    apiClient,
    `/customers/${encodeURIComponent(id)}/overview?${searchParams.toString()}`,
  );
};

/**
 * Creates a new customer
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that will own the customer
 * @param customer - Customer data to create
 * @returns Promise that resolves to the created customer
 */
const create = (
  apiClient: ApiClient,
  fleetId: string,
  customer: CreateCustomerPayload,
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString ? `/customers?${queryString}` : "/customers";

  return clientWithApiClient<Response<Customer>>(apiClient, url, {
    method: "POST",
    body: JSON.stringify({ customer }),
  });
};

/**
 * Updates an existing customer
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the customer
 * @param id - Unique identifier of the customer to update
 * @param customer - Updated customer data
 * @returns Promise that resolves to the updated customer
 */
const update = (
  apiClient: ApiClient,
  fleetId: string,
  id: string,
  customer: UpdateCustomerPayload,
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString
    ? `/customers/${id}?${queryString}`
    : `/customers/${id}`;

  return clientWithApiClient<Response<Customer>>(apiClient, url, {
    method: "PUT",
    body: JSON.stringify({ customer }),
  });
};

/**
 * Deletes a customer (soft delete)
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the customer
 * @param id - Unique identifier of the customer to delete
 * @returns Promise that resolves to the deleted customer
 */
const remove = (apiClient: ApiClient, fleetId: string, id: string) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString
    ? `/customers/${id}?${queryString}`
    : `/customers/${id}`;

  return clientWithApiClient<Response<Customer>>(apiClient, url, {
    method: "DELETE",
  });
};

/** The server caps `page_size` at 100. */
const FIND_BY_EMAIL_PAGE_SIZE = 100;
/**
 * The server search is a substring match on name or email, so the full
 * address of one customer matches only a handful of rows. This bounds the
 * walk should a fleet ever have more.
 */
const FIND_BY_EMAIL_MAX_PAGES = 10;

/**
 * Finds the active customer whose email equals `email`, ignoring case and
 * surrounding whitespace.
 *
 * Unlike scanning the first page of `list`, this asks the server to search by
 * email and walks the pages of the result, so the answer does not depend on
 * how many customers the fleet has. Resolves to `null` when nobody matches.
 */
const findByEmail = async (
  apiClient: ApiClient,
  fleetId: string,
  email: string,
): Promise<Customer | null> => {
  const wanted = email.trim().toLowerCase();
  if (!wanted) return null;

  for (let page = 1; page <= FIND_BY_EMAIL_MAX_PAGES; page++) {
    const response = await list(apiClient, fleetId, {
      search: email.trim(),
      page,
      pageSize: FIND_BY_EMAIL_PAGE_SIZE,
    });

    const match = response.data?.find(
      (customer) => customer.email?.trim().toLowerCase() === wanted,
    );
    if (match) return match;

    if (page >= (response.meta?.total_pages ?? 0)) break;
  }

  return null;
};

export const customers = {
  list,
  findByEmail,
  exportCsv,
  get,
  overview,
  create,
  update,
  remove,
};

// Customer Tags API

const listTags = (apiClient: ApiClient, fleetId: string) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);
  return clientWithApiClient<ListResponse<CustomerTag>>(
    apiClient,
    `/customer-tags?${searchParams.toString()}`,
  );
};

const createTag = (
  apiClient: ApiClient,
  fleetId: string,
  tag: CreateCustomerTagPayload,
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);
  return clientWithApiClient<Response<CustomerTag>>(
    apiClient,
    `/customer-tags?${searchParams.toString()}`,
    {
      method: "POST",
      body: JSON.stringify({ tag }),
    },
  );
};

const updateTag = (
  apiClient: ApiClient,
  fleetId: string,
  id: string,
  tag: UpdateCustomerTagPayload,
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);
  return clientWithApiClient<Response<CustomerTag>>(
    apiClient,
    `/customer-tags/${id}?${searchParams.toString()}`,
    {
      method: "PUT",
      body: JSON.stringify({ tag }),
    },
  );
};

const deleteTag = (apiClient: ApiClient, fleetId: string, id: string) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);
  return clientWithApiClient<void>(
    apiClient,
    `/customer-tags/${id}?${searchParams.toString()}`,
    { method: "DELETE" },
  );
};

const assignTags = (
  apiClient: ApiClient,
  fleetId: string,
  customerId: string,
  tagIds: string[],
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);
  return clientWithApiClient<Response<Customer>>(
    apiClient,
    `/customers/${customerId}/tags?${searchParams.toString()}`,
    {
      method: "POST",
      body: JSON.stringify({ customer_id: customerId, tag_ids: tagIds }),
    },
  );
};

export const customerTags = {
  list: listTags,
  create: createTag,
  update: updateTag,
  remove: deleteTag,
  assign: assignTags,
};

// Customer Segments API

const listSegments = (apiClient: ApiClient, fleetId: string) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);
  return clientWithApiClient<ListResponse<CustomerSegment>>(
    apiClient,
    `/customer-segments?${searchParams.toString()}`,
  );
};

const getSegment = (apiClient: ApiClient, fleetId: string, id: string) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);
  return clientWithApiClient<Response<CustomerSegment>>(
    apiClient,
    `/customer-segments/${id}?${searchParams.toString()}`,
  );
};

const createSegment = (
  apiClient: ApiClient,
  fleetId: string,
  segment: CreateCustomerSegmentPayload,
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);
  return clientWithApiClient<Response<CustomerSegment>>(
    apiClient,
    `/customer-segments?${searchParams.toString()}`,
    {
      method: "POST",
      body: JSON.stringify({ segment }),
    },
  );
};

const updateSegment = (
  apiClient: ApiClient,
  fleetId: string,
  id: string,
  segment: UpdateCustomerSegmentPayload,
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);
  return clientWithApiClient<Response<CustomerSegment>>(
    apiClient,
    `/customer-segments/${id}?${searchParams.toString()}`,
    {
      method: "PUT",
      body: JSON.stringify({ segment }),
    },
  );
};

const deleteSegment = (apiClient: ApiClient, fleetId: string, id: string) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);
  return clientWithApiClient<void>(
    apiClient,
    `/customer-segments/${id}?${searchParams.toString()}`,
    { method: "DELETE" },
  );
};

export const customerSegments = {
  list: listSegments,
  get: getSegment,
  create: createSegment,
  update: updateSegment,
  remove: deleteSegment,
};

const listNotes = (
  apiClient: ApiClient,
  fleetId: string,
  customerId: string,
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  return clientWithApiClient<ListResponse<CustomerNote>>(
    apiClient,
    `/customers/${customerId}/notes?${searchParams.toString()}`,
  );
};

const createNote = (
  apiClient: ApiClient,
  fleetId: string,
  customerId: string,
  note: CreateCustomerNotePayload,
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  return clientWithApiClient<Response<CustomerNote>>(
    apiClient,
    `/customers/${customerId}/notes?${searchParams.toString()}`,
    {
      method: "POST",
      body: JSON.stringify({ note }),
    },
  );
};

const updateNote = (
  apiClient: ApiClient,
  fleetId: string,
  customerId: string,
  noteId: string,
  note: UpdateCustomerNotePayload,
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  return clientWithApiClient<Response<CustomerNote>>(
    apiClient,
    `/customers/${customerId}/notes/${noteId}?${searchParams.toString()}`,
    {
      method: "PUT",
      body: JSON.stringify({ note }),
    },
  );
};

const deleteNote = (
  apiClient: ApiClient,
  fleetId: string,
  customerId: string,
  noteId: string,
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  return clientWithApiClient<Response<CustomerNote>>(
    apiClient,
    `/customers/${customerId}/notes/${noteId}?${searchParams.toString()}`,
    { method: "DELETE" },
  );
};

export const customerNotes = {
  list: listNotes,
  create: createNote,
  update: updateNote,
  remove: deleteNote,
};

// Customer Interactions API (unified activity timeline)

const listInteractions = (
  apiClient: ApiClient,
  fleetId: string,
  customerId: string,
  params?: ListInteractionsParams,
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);
  if (params?.types) searchParams.append("types", params.types);
  if (params?.before) searchParams.append("before", params.before);
  if (params?.pageSize)
    searchParams.append("page_size", String(params.pageSize));

  return clientWithApiClient<ListInteractionsResponse>(
    apiClient,
    `/customers/${customerId}/interactions?${searchParams.toString()}`,
  );
};

export const customerInteractions = {
  list: listInteractions,
};
